import { useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import { Dropdown, IconButton, Icon, icon as iconSize } from '@nula/core-ui';
import { deleteBlockAt, duplicateBlockAt } from './block-actions';
import styles from './editor-block-handle.module.css';

export interface EditorBlockHandleProps {
  editor: Editor;
}

export function EditorBlockHandle({ editor }: EditorBlockHandleProps) {
  const posRef = useRef<number>(0);
  const I = iconSize.sm;

  // Must be referentially stable: DragHandle lists onNodeChange in its register-plugin
  // effect deps, so an inline arrow re-registers the plugin every render — which
  // y-prosemirror amplifies into a doc-transaction render loop.
  const handleNodeChange = useCallback(({ pos }: { pos: number }) => {
    posRef.current = pos;
  }, []);

  return (
    <DragHandle
      editor={editor}
      className={styles.handle}
      nested
      onNodeChange={handleNodeChange}
    >
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            { key: 'duplicate', label: 'Duplicate' },
            { key: 'delete', label: 'Delete', danger: true },
          ],
          onClick: (key) => {
            if (key === 'duplicate') {
              duplicateBlockAt(editor, posRef.current);
            } else if (key === 'delete') {
              deleteBlockAt(editor, posRef.current);
            }
          },
        }}
      >
        <IconButton size="sm" transparent aria-label="Block actions">
          <Icon name="drag_indicator" size={I} />
        </IconButton>
      </Dropdown>
    </DragHandle>
  );
}
